import type { ReactNode } from 'react';
import type { Mode } from '../api/types';
import { MODES } from '../api/types';
import { MODE_LABEL, type Tone } from '../lib/labels';
import { errorText } from './feedback';

export function ModeChip({ mode, size = 'md' }: { mode: Mode | null | undefined; size?: 'md' | 'lg' }) {
  if (!mode) return <span className="chip chip-neutral">—</span>;
  return (
    <span className={`chip mode-${mode} ${size === 'lg' ? 'chip-lg' : ''}`}>
      <span className="dot" aria-hidden="true" />
      {MODE_LABEL[mode]}
    </span>
  );
}

export function Badge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Card({ title, actions, children, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-head">
          {title && <h2 className="card-title">{title}</h2>}
          {actions && <div className="row gap-s">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="muted">{subtitle}</p>}
      </div>
      {actions && <div className="row gap-s wrap">{actions}</div>}
    </header>
  );
}

export function Loading({ label = 'Chargement…' }: { label?: string }) {
  return <p className="muted small" role="status">{label}</p>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function QueryError({ error }: { error: unknown }) {
  return <p className="alert alert-bad" role="alert">{errorText(error)}</p>;
}

/** Affiche chargement / erreur / contenu pour un résultat de useQuery. */
export function Async<T>({ q, children, empty }: { q: { data: T | undefined; isLoading: boolean; error: unknown }; children: (d: T) => ReactNode; empty?: ReactNode }) {
  if (q.isLoading) return <Loading />;
  if (q.error) return <QueryError error={q.error} />;
  if (q.data === undefined || q.data === null || (Array.isArray(q.data) && q.data.length === 0)) return <>{empty ?? <Empty>Rien à afficher.</Empty>}</>;
  return <>{children(q.data)}</>;
}

export function ModePicker({ value, onChange, name }: { value: Mode; onChange: (m: Mode) => void; name: string }) {
  return (
    <div className="mode-picker" role="radiogroup" aria-label={name}>
      {MODES.map((m) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={value === m}
          className={`mode-option mode-${m} ${value === m ? 'is-on' : ''}`}
          onClick={() => onChange(m)}
        >
          <span className="dot" aria-hidden="true" />
          {MODE_LABEL[m]}
        </button>
      ))}
    </div>
  );
}

export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function Segmented<T extends string | number>({ options, value, onChange, label }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" role="radio" aria-checked={o.value === value} className={o.value === value ? 'is-on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function KV({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v], i) => (
        <div key={i}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
